import {
    ThemeProvider as MuiThemeProvider,
    createTheme,
} from "@mui/material/styles";
import classNames from "classnames";
import React, { forwardRef, ReactElement, useCallback, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { NumericFormat, InputAttributes } from "react-number-format";
import { ThemeProvider } from "styled-components";

import useDocumentLanguage from "hooks/useDocumentLanguage";
import evaluteVisibilityFromDependencies from "utils/evaluteVisibilityFromDependencies";
import generateValidationRules from "utils/generateValidationRules";

import {
    Wrapper,
    StyledSlider,
    Input,
    InputAdornment,
    Value,
    Label,
    HelperText,
} from "./Slider.styled";
import SliderProps, { NumberFormatProps } from "./SliderProps";

const Slider = ({
    id,
    identifier,
    label,
    ariaLabel,
    elementName,
    step,
    min,
    max,
    disabled,
    adornment,
    input = false,
    inputFormatNumber = true,
    predefinedValueNumber,
    displayOption = "Full",
    marks,
    valueLabelDisplay = "on",
    className,
    style,
    description,
    validators,
    dependencies,
    control,
    size = "medium",
    onChange,
    onFocus,
    setValue,
}: SliderProps): ReactElement => {
    const documentLanguage = useDocumentLanguage();
    const formContext = useFormContext();
    const unregister = formContext?.unregister || undefined;

    const visibleFromDependencies =
        dependencies && control
            ? evaluteVisibilityFromDependencies(dependencies, control)
            : true;

    const classes = classNames(
        className,
        validators?.map((validator) => {
            return validator?.model?.validationCssClass || "";
        }),
    );

    const rules =
        validators && validators?.length > 0
            ? generateValidationRules(validators)
            : undefined;

    const [values, setValues] = React.useState<
        number | Array<number> | undefined
    >(predefinedValueNumber);

    const handleSliderChange = (
        event: Event,
        value: number | number[],
        activeThumb: number,
        callback: (...event: any[]) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
    ) => {
        setValues(value);
        callback && callback(value);
        onChange && onChange(event, value, activeThumb);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        callback: (...event: any[]) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
    ) => {
        const value =
            event.target.value === "" ? min || 0 : Number(event.target.value);
        setValues(value);
        callback && callback(value);
        onChange && onChange(event, value);
    };

    const handleBlur = useCallback(
        (
            callback?: (...event: any[]) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
        ) => {
            if (values && min && !Array.isArray(values) && values < min) {
                setValues(min);
                onChange && onChange(undefined, min);
            } else if (
                values &&
                max &&
                !Array.isArray(values) &&
                values > max
            ) {
                setValues(max);
                onChange && onChange(undefined, max);
            }
            callback && callback();
        },
        [min, max, values, onChange],
    );

    useEffect(() => {
        handleBlur();
    }, [min, max]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (elementName && setValue && unregister)
            if (visibleFromDependencies) {
                setValue(elementName, predefinedValueNumber);
                setValues(predefinedValueNumber);
            } else {
                unregister(elementName);
                setValues(undefined);
            }
    }, [
        elementName,
        unregister,
        predefinedValueNumber,
        setValue,
        visibleFromDependencies,
    ]);

    // If the formstate updates, make sure to update the internal state to reflect the changes
    const elementFormState =
        elementName && !!formContext && formContext.watch(elementName);
    useEffect(() => {
        if (formContext && elementFormState !== values) {
            setValues(elementFormState);
        }
    }, [elementFormState, values, formContext]);

    return (
        <MuiThemeProvider theme={createTheme()}>
            <ThemeProvider
                theme={{
                    displayOption: displayOption,
                    hasInput: input,
                    size,
                }}
            >
                {visibleFromDependencies && (
                    <Wrapper>
                        {label && (
                            <Label
                                htmlFor={id || identifier}
                                disabled={disabled}
                            >
                                {label}
                            </Label>
                        )}
                        <Controller
                            name={elementName || ""}
                            control={control}
                            rules={rules}
                            disabled={disabled}
                            defaultValue={predefinedValueNumber}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <StyledSlider
                                        aria-label={ariaLabel || label}
                                        step={step}
                                        min={min}
                                        max={max}
                                        disabled={disabled}
                                        marks={marks}
                                        valueLabelDisplay={valueLabelDisplay}
                                        className={classes}
                                        style={style}
                                        {...field}
                                        onChange={(
                                            event: Event,
                                            value: number | number[],
                                            activeThumb: number,
                                        ) =>
                                            handleSliderChange(
                                                event,
                                                value,
                                                activeThumb,
                                                field.onChange,
                                            )
                                        }
                                        onFocus={(
                                            event: React.FocusEvent<HTMLInputElement>,
                                        ) => {
                                            if (onFocus) {
                                                onFocus(event);
                                            }
                                        }}
                                        value={
                                            values !== undefined ? values : -1
                                        } // -1 if undefined, avoiding uncontrolled to controlled error
                                    />
                                    {input && (
                                        <Input
                                            id={id || identifier}
                                            value={
                                                values !== undefined
                                                    ? values
                                                    : "" // "" if undefined, avoiding uncontrolled to controlled error
                                            }
                                            disabled={disabled}
                                            onChange={(event) =>
                                                handleInputChange(
                                                    event,
                                                    field.onChange,
                                                )
                                            }
                                            size="small"
                                            variant="outlined"
                                            onBlur={() =>
                                                handleBlur(field.onBlur)
                                            }
                                            inputProps={{
                                                inputMode: "numeric",
                                            }}
                                            InputProps={{
                                                inputComponent:
                                                    inputFormatNumber &&
                                                    (NumberFormat as any), // eslint-disable-line @typescript-eslint/no-explicit-any
                                                endAdornment: adornment && (
                                                    <InputAdornment position="end">
                                                        {adornment}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                    {valueLabelDisplay && !input && (
                                        <Value disabled={disabled}>
                                            {values &&
                                                (Array.isArray(values)
                                                    ? `${values[0]} – ${values[1]}`
                                                    : values.toLocaleString(
                                                          documentLanguage ||
                                                              "sv-SE",
                                                      ))}
                                        </Value>
                                    )}
                                    {error && (
                                        <HelperText error={true}>
                                            {error.message}
                                        </HelperText>
                                    )}
                                    {description && (
                                        <HelperText error={false}>
                                            {description}
                                        </HelperText>
                                    )}
                                </>
                            )}
                        />
                    </Wrapper>
                )}
            </ThemeProvider>
        </MuiThemeProvider>
    );
};

const NumberFormat = forwardRef<
    typeof NumericFormat<InputAttributes>,
    NumberFormatProps
>(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator=" "
        />
    );
});

export default React.memo(Slider);
